"use client";

import { c as _c } from "react/compiler-runtime";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { pie as d3Pie } from "d3-shape";
import { Children, useEffect, isValidElement, type ReactElement, type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { defaultPieColors, type PieArcData, type PieContextValue, type PieData, PieProvider } from "./pie-context";

/** Default hover offset in pixels */
export const DEFAULT_HOVER_OFFSET = 10;
export interface PieChartProps {
  /** Data array - each item represents a slice */
  data: PieData[];
  /** Chart size in pixels. If not provided, uses parent container size */
  size?: number;
  /** Inner radius for donut charts. Default: 0 (solid pie) */
  innerRadius?: number;
  /** Padding angle between slices in radians. Default: 0 */
  padAngle?: number;
  /** Corner radius for rounded slice edges. Default: 0 */
  cornerRadius?: number;
  /** Start angle in radians. Default: -PI/2 (top) */
  startAngle?: number;
  /** End angle in radians. Default: 3*PI/2 (full circle from top) */
  endAngle?: number;
  /** Additional class name for the container */
  className?: string;
  /** Controlled hover state - index of hovered slice */
  hoveredIndex?: number | null;
  /** Callback when hover state changes */
  onHoverChange?: (index: number | null) => void;
  /**
   * Hover offset in pixels for slice hover effects.
   * This also determines the padding around the chart to prevent clipping.
   * Default: 10
   */
  hoverOffset?: number;
  /** Child components (PieSlice, PieCenter, patterns, gradients, etc.) */
  children: ReactNode;
}
interface PieChartInnerProps {
  width: number;
  height: number;
  data: PieData[];
  innerRadius: number;
  padAngle: number;
  cornerRadius: number;
  startAngle: number;
  endAngle: number;
  hoverOffset: number;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  hoveredIndexProp?: number | null;
  onHoverChange?: (index: number | null) => void;
}

// Helper to check if a child is a PieCenter component
function isPieCenter(child: ReactNode): boolean {
  return isValidElement(child) && typeof child.type === "function" && ((child.type as {
    displayName?: string;
  }).displayName === "PieCenter" || (child.type as {
    name?: string;
  }).name === "PieCenter");
}

// Helper to check if a component is a gradient or pattern definition
function isDefsComponent(child: ReactElement): boolean {
  const displayName = (child.type as {
    displayName?: string;
  })?.displayName || (child.type as {
    name?: string;
  })?.name || "";
  return displayName.includes("Gradient") || displayName.includes("Pattern") || displayName === "LinearGradient" || displayName === "RadialGradient";
}
function PieChartInner(t0) {
  const $ = _c(60);
  const {
    width,
    height,
    data,
    innerRadius: innerRadiusProp,
    padAngle,
    cornerRadius,
    startAngle,
    endAngle,
    hoverOffset,
    children,
    containerRef,
    hoveredIndexProp,
    onHoverChange
  } = t0;
  const [internalHoveredIndex, setInternalHoveredIndex] = useState(null);
  const [animationKey] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const isControlled = hoveredIndexProp !== undefined;
  const hoveredIndex = isControlled ? hoveredIndexProp : internalHoveredIndex;
  let t1;
  if ($[0] !== isControlled || $[1] !== onHoverChange) {
    t1 = index => {
      if (isControlled) {
        onHoverChange?.(index);
      } else {
        setInternalHoveredIndex(index);
      }
    };
    $[0] = isControlled;
    $[1] = onHoverChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setHoveredIndex = t1;
  const size = Math.min(width, height);
  const center = size / 2;
  const padding = hoverOffset;
  const outerRadius = center - padding;
  const innerRadius = innerRadiusProp;
  let t2;
  let t3;
  if ($[3] !== data) {
    t3 = data.reduce(_temp, 0);
    $[3] = data;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  t2 = t3;
  const totalValue = t2;
  let t4;
  if ($[5] !== data) {
    t4 = index_0 => {
      const item = data[index_0];
      if (item?.color) {
        return item.color;
      }
      return defaultPieColors[index_0 % defaultPieColors.length] as string;
    };
    $[5] = data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const getColor = t4;
  let t5;
  if ($[7] !== data || $[8] !== getColor) {
    t5 = index_1 => {
      const item_0 = data[index_1];
      if (item_0?.fill) {
        return item_0.fill;
      }
      return getColor(index_1);
    };
    $[7] = data;
    $[8] = getColor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const getFill = t5;
  let t6;
  let t7;
  if ($[10] !== data || $[11] !== endAngle || $[12] !== padAngle || $[13] !== startAngle) {
    const pieGenerator = d3Pie().value(_temp2).startAngle(startAngle).endAngle(endAngle).padAngle(padAngle).sort(null);
    const computed = pieGenerator(data);
    t7 = computed.map(_temp3);
    $[10] = data;
    $[11] = endAngle;
    $[12] = padAngle;
    $[13] = startAngle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  t6 = t7 as PieArcData[];
  const arcs = t6;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 100);
      return () => clearTimeout(timer);
    };
    t9 = [];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== children) {
    const svgNodes = [];
    const centerNodes = [];
    const defsNodes = [];
    Children.forEach(children, child => {
      if (!isValidElement(child)) {
        svgNodes.push(child);
        return;
      }
      if (isPieCenter(child)) {
        centerNodes.push(child);
      } else {
        if (isDefsComponent(child)) {
          defsNodes.push(child);
        } else {
          svgNodes.push(child);
        }
      }
    });
    t11 = {
      svgChildren: svgNodes,
      centerChildren: centerNodes,
      defsChildren: defsNodes
    };
    $[17] = children;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  t10 = t11;
  const {
    svgChildren,
    centerChildren,
    defsChildren
  } = t10;
  if (size < 10) {
    return null;
  }
  let t12;
  if ($[19] !== animationKey || $[20] !== arcs || $[21] !== center || $[22] !== containerRef || $[23] !== cornerRadius || $[24] !== data || $[25] !== getColor || $[26] !== getFill || $[27] !== hoverOffset || $[28] !== hoveredIndex || $[29] !== innerRadius || $[30] !== isLoaded || $[31] !== outerRadius || $[32] !== padAngle || $[33] !== setHoveredIndex || $[34] !== size || $[35] !== totalValue) {
    t12 = {
      data,
      arcs,
      size,
      center,
      outerRadius,
      innerRadius,
      padAngle,
      cornerRadius,
      hoverOffset,
      hoveredIndex,
      setHoveredIndex,
      animationKey,
      isLoaded,
      containerRef,
      totalValue,
      getColor,
      getFill
    };
    $[19] = animationKey;
    $[20] = arcs;
    $[21] = center;
    $[22] = containerRef;
    $[23] = cornerRadius;
    $[24] = data;
    $[25] = getColor;
    $[26] = getFill;
    $[27] = hoverOffset;
    $[28] = hoveredIndex;
    $[29] = innerRadius;
    $[30] = isLoaded;
    $[31] = outerRadius;
    $[32] = padAngle;
    $[33] = setHoveredIndex;
    $[34] = size;
    $[35] = totalValue;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const contextValue = t12;
  let t13;
  if ($[37] !== size) {
    t13 = {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr",
      width: size,
      height: size
    };
    $[37] = size;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      gridArea: "1 / 1"
    };
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== defsChildren) {
    t15 = defsChildren.length > 0 && <defs>{defsChildren}</defs>;
    $[40] = defsChildren;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== svgChildren) {
    t16 = <>{svgChildren}</>;
    $[42] = svgChildren;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== center || $[45] !== t16) {
    t17 = <Group left={center} top={center}>{t16}</Group>;
    $[44] = center;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== size || $[48] !== t15 || $[49] !== t17) {
    t18 = <svg aria-hidden="true" height={size} style={t14} width={size}>{t15}{t17}</svg>;
    $[47] = size;
    $[48] = t15;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== centerChildren) {
    t19 = centerChildren.length > 0 && <div className="pointer-events-none flex items-center justify-center" style={{
      gridArea: "1 / 1"
    }}>{centerChildren}</div>;
    $[51] = centerChildren;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t13 || $[54] !== t18 || $[55] !== t19) {
    t20 = <div className="grid" style={t13}>{t18}{t19}</div>;
    $[53] = t13;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== contextValue || $[58] !== t20) {
    t21 = <PieProvider value={contextValue}>{t20}</PieProvider>;
    $[57] = contextValue;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  return t21;
}
function _temp3(arc, index_2) {
  return {
    data: arc.data,
    index: index_2,
    startAngle: arc.startAngle,
    endAngle: arc.endAngle,
    padAngle: arc.padAngle,
    value: arc.value
  };
}
function _temp2(d_0) {
  return d_0.value;
}
function _temp(sum, d) {
  return sum + d.value;
}
export function PieChart({
  data,
  size: fixedSize,
  innerRadius = 0,
  padAngle = 0,
  cornerRadius = 0,
  startAngle = -Math.PI / 2,
  endAngle = 3 * Math.PI / 2,
  className = "",
  hoveredIndex,
  onHoverChange,
  hoverOffset = DEFAULT_HOVER_OFFSET,
  children
}: PieChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // If fixed size is provided, use it directly
  if (fixedSize) {
    return <div className={cn("relative flex items-center justify-center", className)} ref={containerRef} style={{
      width: fixedSize,
      height: fixedSize
    }}>
        <PieChartInner containerRef={containerRef} cornerRadius={cornerRadius} data={data} endAngle={endAngle} height={fixedSize} hoveredIndexProp={hoveredIndex} hoverOffset={hoverOffset} innerRadius={innerRadius} onHoverChange={onHoverChange} padAngle={padAngle} startAngle={startAngle} width={fixedSize}>
          {children}
        </PieChartInner>
      </div>;
  }

  // Otherwise use ParentSize for responsive sizing
  return <div className={cn("relative aspect-square w-full", className)} ref={containerRef}>
      <ParentSize debounceTime={10}>
        {({
        width,
        height
      }) => <PieChartInner containerRef={containerRef} cornerRadius={cornerRadius} data={data} endAngle={endAngle} height={height} hoveredIndexProp={hoveredIndex} hoverOffset={hoverOffset} innerRadius={innerRadius} onHoverChange={onHoverChange} padAngle={padAngle} startAngle={startAngle} width={width}>
            {children}
          </PieChartInner>}
      </ParentSize>
    </div>;
}
PieChart.displayName = "PieChart";
export default PieChart;